<template>
  <NuxtLayout name="cart">
    <template #top>
      <h1 class="sr-only">
        Varukorg steg {{ checkoutStore.ui.currentStep }}:
        {{ checkoutStore.currentCheckoutStepName }}
      </h1>
      <StepIndicator
        :current-step="checkoutStore.ui.currentStep"
        :steps="checkoutStore.steps"
        @step-counter="checkoutStore.ui.currentStep = $event"
      />
    </template>

    <template #formsteps-column>
      <div
        v-if="
          checkoutStore.currentCheckoutStepName === CHECKOUT_STEP_1_NAME &&
          !checkoutStore.isCartEmpty
        "
        class="stage-1"
      >
        <CheckoutStep1 />
      </div>

      <div
        v-else-if="
          checkoutStore.currentCheckoutStepName === CHECKOUT_STEP_2_NAME &&
          !checkoutStore.isCartEmpty
        "
        class="stage-2"
      >
        <CheckoutStep2 />
      </div>

      <div
        v-else-if="
          checkoutStore.currentCheckoutStepName === CHECKOUT_STEP_3_NAME &&
          !checkoutStore.isCartEmpty
        "
        class="stage-3"
      >
        <CheckoutStep3 />
      </div>

      <div
        v-else-if="
          checkoutStore.currentCheckoutStepName === CHECKOUT_STEP_4_NAME &&
          !checkoutStore.isCartEmpty
        "
        class="stage-4"
      >
        <CheckoutStep4 />
      </div>

      <div v-if="checkoutStore.isCartEmpty">
        <GlobalNotificationContainer
          :show="!!notifications.title"
          :is-just-text-row="true"
        />
      </div>
    </template>
    <template #cart-column>
      <CheckoutAppCart
        :deduplicated-applied-discounts="deduplicatedAppliedDiscounts"
        :fetch-pending="status === 'idle' || status === 'pending'"
        :fetch-status="status"
        @on-should-refresh="onShouldRefreshCart"
      />
    </template>
  </NuxtLayout>
</template>

<script lang="ts" setup>
import {
  CHECKOUT_STEP_1_NAME,
  CHECKOUT_STEP_2_NAME,
  CHECKOUT_STEP_3_NAME,
  CHECKOUT_STEP_4_NAME,
} from '~/models/checkout/CheckoutSteps.model';
import { APP_CART_DATA_KEY } from '~/models/AsyncDataKeys.model';
import type { AppliedDiscount } from '~/models/shopping_cart/ShoppingCart.api.model';
import { general_setPageTheme } from '~/utils/general/pageThemeClassFromMarkup';
import { useBindingPeriodNotification } from '~/composables/checkout/useBindingPeriodNotification';
import { checkout_sendCheckoutLogToBackend } from '~/utils/checkout/sendCheckoutLogToBackend';
const checkoutStore = useMyCheckoutStore();
const notifications = useNotificationStore();
notifications.reset();
const config = useRuntimeConfig();
const route = useRoute();
const { $api } = useNuxtApp();

definePageMeta({
  layout: false,
});

const {
  refresh,
  status,
  error: fetchCarterror,
} = await useAsyncData(
  APP_CART_DATA_KEY,
  async () => {
    return await checkoutStore.fetchCart();
  },
  { server: false, lazy: true },
);

if (fetchCarterror.value) {
  logError(fetchCarterror.value);
  notifications.setStandardErrorNotification();
}

useBindingPeriodNotification();

const deduplicateAppliedDiscounts = (discounts?: AppliedDiscount[]) => {
  if (!discounts) {
    return [];
  }

  const seen = new Set();

  return discounts.filter((discount) => {
    if (seen.has(discount.Id)) {
      return false;
    }

    seen.add(discount.Id);
    return true;
  });
};

const deduplicatedAppliedDiscounts = computed(() =>
  deduplicateAppliedDiscounts(checkoutStore.fetchedData.cart.appliedDiscounts),
);

function checkForPaymentError() {
  const route = useRoute();
  const errorKey = route.query?.error as string;
  if (errorKey?.toLowerCase() === 'netaxept') {
    notifications.setErrorFromLookup(errorKey);
  }
}

function onShouldRefreshCart() {
  refresh();
}

onBeforeMount(() => {
  checkForPaymentError();
});

general_setPageTheme();

const canonicalUrl = `${config.public.apiBaseUrl.replace(/\/$/, '')}${route.fullPath.split('?')[0]}`;

const seoInput = {
  seoData: {
    title: '',
    description: '',
    siteName: '',
    keywords: '',
    canonicalUrl,
  },
  fallBackSeoData: {
    title: 'Varukorg | Halebop.se',
    description:
      'Fixa Halebop idag, då får du fria SMS till hela världen och massor med surf.  Vi har de senaste Mobiltelefonerna från Apple, Samsung, Nokia, Sony och många fler',
    siteName: 'Halebop',
    keywords: '',
    canonicalUrl,
  },
};
useCustomSeoMeta(seoInput);

const hasInitialized = ref(false);

watch(
  () => checkoutStore.ui.currentStep,
  async (newStep, oldStep) => {
    const cartItemIds = `${checkoutStore.fetchedData.cart.cartItems.flatMap((x) => x.Id).join(', ')}`;
    if (!hasInitialized.value) {
      hasInitialized.value = true;

      checkout_sendCheckoutLogToBackend(
        'Shop',
        `Checkout step: ${newStep}`,
        `Checkout init. Current cart item ids: ${cartItemIds}`,
      );

      // First time set — fires immediately on mount with the current step value
      return;
    }

    checkout_sendCheckoutLogToBackend(
      'Shop',
      `Checkout step: ${newStep}`,
      `Current cart item ids: ${cartItemIds}`,
    );
  },
  { immediate: true },
);
</script>

<style scoped lang="scss"></style>
